<template>
  <main class="home">
    <section class="hero" ref="hero">
      <Info class="u-hidden--from-ipadP" :allways="true"/>
      <VideoAlpha class="hero__video" data-item="video"/>
      <AppTitle
        class="hero__title c-title--light"
        :data="{ content: pageData.hero.title }"
        :isAnimated="false"
        data-item="title"
      />
      <AppLine class="hero__line" :isAnimated="false" data-item="line"/>
      <AppTitle
        class="hero__title c-title--dark"
        type="tube"
        :data="{ content: pageData.hero.subtitle }"
        :autoPlay="true"
        :isAnimated="false"
        data-item="title"
      />
      <AppLine class="hero__line" :isAnimated="false" data-item="line"/>
      <AppEyelet
        class="hero__eyelet u-hidden--until-ipadP"
        :data="{ content: '😉 Scroll to discover' }"
        :isAnimated="false"
        data-item="eyelet"
      />
    </section>

    <section class="intro">
      <div
        class="intro__video"
        data-animation="fadeVideo"
        ref="videoIntro"
      >
        <video
          autoplay
          muted
          loop
        >
          <source :src="`/videos/${$route.params.city}.mp4`" type="video/mp4">
        </video>
      </div>
      <AppEyelet :data="{ content: pageData.intro.eyelet }" />
      <AppLine :isSmall="true"/>
      <AppTitle
        class="intro__title c-title--light"
        type="marquee"
        :data="{ content: pageData.intro.marquee[0] }"
      />
      <AppLine :isSmall="true"/>
      <AppTitle
        class="intro__title c-title--dark"
        type="marquee"
        :data="{ content: pageData.intro.marquee[1] }"
      />
      <AppLine :isSmall="true"/>
      <AppTitle
        class="intro__title c-title--light"
        type="marquee"
        :data="{ content: pageData.intro.marquee[2] }"
      />
      <AppLine :isSmall="true"/>
      <div class="intro__button-cont">
        <AppButton :data="pageData.intro.button" :router="false" />
      </div>
    </section>

    <HomeSlider :data="speakers" type="speakers" />

    <AppShowcase type="schedule" />

    <HomeSlider :data="workshops" type="workshops" />

    <AppShowcase type="venues" />

    <AppSponsor :sponsorList="getSponsor" />

    <AppFooter />
  </main>
</template>

<script>
import { mapGetters } from 'vuex';

import AppButton from '@/components/AppButton.vue';
import AppLine from '@/components/AppLine.vue';
import AppEyelet from '@/components/AppEyelet.vue';
import AppTitle from '@/components/AppTitle.vue';
import AppShowcase from '@/components/AppShowcase.vue';
import AppSponsor from '@/components/AppSponsor.vue';
import AppFooter from '@/components/AppFooter.vue';
import Info from '@/components/Info.vue';
import VideoAlpha from '@/components/VideoAlpha.vue';
import HomeSlider from '@/components/HomeSlider.vue';

import medusa from '@/scripts/medusa';
import { initials, animations } from '@/scripts/fx';

import home from '@/assets/data/contents/home.json';

export default {
  name: 'Home',
  components: {
    AppButton,
    AppLine,
    AppTitle,
    AppEyelet,
    HomeSlider,
    AppShowcase,
    AppSponsor,
    AppFooter,
    VideoAlpha,
    Info,
  },
  data() {
    return {
      home,
    };
  },
  computed: {
    ...mapGetters(['currentPage', 'contentReady']),
    pageData() {
      return this.home[this.$route.params.city];
    },
    speakers() {
      const { speakers } = this.pageData;
      return speakers;
    },
    workshops() {
      const { workshops } = this.pageData;
      return workshops;
    },
    getSponsor() {
      const { sponsors } = this.pageData;
      return sponsors;
    },
  },
  mounted() {
    this.enterAnimation();

    this.calcVideoH();
    this.$eventHub.$on('windowResize', this.calcVideoH);

    medusa.recalculateTargets(this.$el);
  },
  methods: {
    enterAnimation() {
      const titles = [...this.$refs.hero.querySelectorAll('[data-item="title"] h3')];
      const lines = [...this.$refs.hero.querySelectorAll('[data-item="line"]')];
      const video = this.$refs.hero.querySelector('[data-item="video"]');
      const eyelet = this.$refs.hero.querySelector('[data-item="eyelet"]');
      lines.forEach(line => initials.get('line')(line));
      titles.forEach(title => initials.get('title')(title));
      initials.get('video')(video, 0);
      initials.get('fade')(eyelet, 0);

      animations.get('title')(titles[0], 100);
      animations.get('line')(lines[0], 400);
      animations.get('title')(titles[1], 500);
      animations.get('line')(lines[1], 800);
      animations.get('video')(video, 1000);
      animations.get('fade')(eyelet, 1300).finished.then(() => {
        eyelet.style.transform = '';
        eyelet.style.opacity = '';
        eyelet.setAttribute('data-animated', '');
      });
    },
    calcVideoH() {
      const el = this.$refs.videoIntro;
      if (el !== undefined) el.style.marginTop = `-${el.getBoundingClientRect().height / 2}px`;
    },
  },
  beforeDestroy() {
    this.$eventHub.$off('windowResize', this.calcVideoH);
    medusa.removePrevTargets();
  },
};
</script>

<style lang="scss" scoped>
.home {
  .hero {
    &__line {
      z-index: -1;
    }

    .info {
      position: relative;
      top: unset;
      width: 100%;

      transform: translateY($unit-vh * -2);
      z-index: unset;
    }

    &__eyelet[data-animated] {
      transition: opacity 0.5s $outBack 3s,
        transform 0.5s $outBack 3s;
      transform: translateY(-20px);
      opacity: 0;
    }
  }

  .intro {
    &__video {
      position: absolute;
      top: 50%;
      left: 50%;
      width: $unit-vw * 52;
      margin-left: $unit-vw * -26;

      background-color: var(--color-bg);

      z-index: -1;

      @include mq(ipadP) {
        width: $unit-vw * 42;
        margin-left: $unit-vw * -21;
      }

      @include mq(ipadL) {
        width: $unit-vw * 32;
        margin-left: $unit-vw * -16;
      }

      video {
        width: 100%;
        opacity: 0.35;
        mix-blend-mode: overlay;
      }
    }

    &__button-cont {
      position: relative;
      width: 100%;

      @include mq($until: ipadP) {
        text-align: center;
        margin-left: 0 !important;
      }

      @include mq($until: ipadL, $and: $is-touch-device) {
        margin-left: 20px;
      }
    }
  }
}
</style>
